import React, { useState } from 'react';
import { Box, ContentTemplate, Grid, Progress, SectionHeader, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { AxiosError } from 'axios';
import {
  useGetReviewExamAmendment,
  usePostPayExamAmendment,
} from '../../../api/candidate/exam/@candidate/queries-services/queries';
import PersonalDetails from '../../examRegistration/components/particulars/PersonalDetails';
import ContactDetails from '../../examRegistration/components/particulars/ContactDetails';
import NextOfKinContactDetails from '../../examRegistration/components/particulars/NextOfKinContactDetails';
import PaymentButton, { PaymentMethod } from '../../payment/components/PaymentButton';
import { fallbackEmptyString, parseLocationSearch } from '../../../utils/StringUtils';
import RouteKey from '../../../routes/key';
import AmendedSubjectExamFees from '../../subject/components/AmendedSubjectExamFees';
import { GenericErrorResponse } from '../../../api';
import { PaymentLinkRequest } from '../../../api/candidate/exam/model';
import { GET_USER_DETAILS_QUERY_KEY } from '../../../constants/queryKeys';
import { useGetUserDetails } from '../../../api-refactored/user/useGetUserDetails';
import { selectIsUserLoggedIn } from '../../../store/appSlice';
import { usePostPaymentAccess } from '../../../api-refactored/financial/usePostPaymentAccess';

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
    },
  },
  pleaseLogoutText: {
    marginBottom: theme.spacing(6),
  },
}));

const { invalid, receipt, amendmentRegistration } = RouteKey['payment-link'].child;
const { expired, used } = RouteKey['payment-link'].child;
const { paymentLink } = RouteKey.child;

const AmendmentPaymentLink = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();

  const location = useLocation<PaymentLinkRequest>();

  const { token } = parseLocationSearch(location.search);
  const { uuid: applicationUUID } = useParams<any>();

  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const [isSessionDone, setIsSessionDone] = useState(false);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  const { isLoading: isLoadingUserDetails } = useGetUserDetails();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);

  const errorCallback = (err: AxiosError<GenericErrorResponse>) => {
    const errorCode = err.response?.data.code;

    if (errorCode === '1017') {
      history.push(`/${paymentLink}/${amendmentRegistration}/${used}`);
    } else if (errorCode === '1014') {
      history.push(`/${paymentLink}/${amendmentRegistration}/${expired}`);
    } else if (errorCode === '1019') {
      history.push(`/${paymentLink}/${amendmentRegistration}/${invalid}`);
    }
  };

  const { data, isError, isLoading } = useGetReviewExamAmendment(applicationUUID, {
    token,
    onError: errorCallback,
    enabled: isSessionDone,
  });

  const invalidSessionCallback = () => {
    setIsSessionDone(true);
    queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
    return false;
  };

  useGetReviewExamAmendment(applicationUUID, {
    token,
    onSuccess: () =>
      mutatePostPaymentAccess({
        registrationUuid: applicationUUID,
      }),
    onError: errorCallback,
    onSettled: invalidSessionCallback,
  });

  const { mutate: mutatePostPaymentAccess } = usePostPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const examAmendment = usePostPayExamAmendment();

  const handleSuccess = () => {
    history.push(`/${paymentLink}/${amendmentRegistration}/${applicationUUID}/${receipt}`);
  };

  const handlePaymentOption = (
    callback: (internalTransactionRef: string) => void,
    method?: string
  ) => {
    examAmendment.mutate(
      {
        uuid: applicationUUID,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (res) => {
          if (res.data.paymentSuccess === true) {
            handleSuccess();
          }
          callback(res.data.internalTransactionRef || '');
        },
        onError: () => {
          callback('');
        },
      }
    );
  };

  const handleClickPaymentButton = (callback: () => void) => {
    callback();
  };

  if (isError) {
    return (
      <ContentTemplate>
        <Typography>An unexpected error occurred. Please refresh the page.</Typography>
      </ContentTemplate>
    );
  }

  if (isUserLoggedIn) {
    return (
      <ContentTemplate>
        <Typography variant="subtitle1" className={classes.pleaseLogoutText}>
          Authenticated session detected in the current browser. Please log out to proceed.
        </Typography>
      </ContentTemplate>
    );
  }

  if (isLoadingUserDetails || isLoading || !data) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ContentTemplate isContinuable type="full">
        <SectionHeader title={t('amendment->>ConfirmAmendmentDetails.Confirm Amendment Details')} />
        <PersonalDetails examCandidateDetail={data.candidateDetails} />
        <ContactDetails contactDetails={data.candidateDetails} />
        <NextOfKinContactDetails {...data.candidateDetails} />
        <AmendedSubjectExamFees {...data} />

        <Grid container className={classes.actionContainer}>
          <Grid container xs={12} md={12} spacing={2}>
            <Grid xs={12} md={4}>
              <PaymentButton
                buttonClassName={classes.paymentButton}
                isUniqueLink={!!token}
                disabled={isPaymentDisabled}
                onPaymentComplete={(isRetry) => {
                  setIsPaymentDisabled(isRetry);
                }}
                paymentMethodsAvailable={paymentAccessDetailsOption}
                onClickPaymentButton={(callback) => {
                  handleClickPaymentButton(callback);
                }}
                onHandlePaymentMethod={(callback, method) => {
                  handlePaymentOption(callback, method);
                }}
                onSuccessPayment={() => {
                  handleSuccess();
                }}
                fullWidth
                buttonText={t('amendment->>ConfirmAmendmentDetails.Proceed with Amendment')}
                buttonVariant="contained"
              />
            </Grid>
          </Grid>
        </Grid>
      </ContentTemplate>
    </>
  );
};

export default AmendmentPaymentLink;
